'use client';
import { Highlight, Image } from '@curology/ui-components-web';
import { Button } from '@curology/ui-components-web/react/Button';
import { Modal } from '@curology/ui-components-web/react/Modal';
import { ModalContent } from '@curology/ui-components-web/react/Modal/ModalContent';
import { ModalFooter } from '@curology/ui-components-web/react/Modal/ModalFooter';
import { Typography } from '@curology/ui-components-web/react/Typography';
import React from 'react';

import { IngredientsAccordion } from './ingredientsAccordion';
import { type ShopifyCartProduct } from '../types';
import { useShopifyProduct } from '../useShopifyProduct';
import { convertSchemaToHtml } from '../utils/convertSchemaToHtml';

export const ProductModal = ({
  open,
  onClose,
  product,
}: {
  open: boolean;
  onClose: () => void;
  product: ShopifyCartProduct;
}) => {
  const {
    displayTitle,
    dialogHero,
    inactiveIngredients,
    activeIngredients,
    ingredientNotes,
    dialogDescription,
    dialogCallout,
  } = useShopifyProduct({ product });

  return (
    <Modal
      onClose={onClose}
      open={open}
      title={
        <Typography
          dangerouslySetInnerHTML={{ __html: displayTitle }}
          className="cur-mt-1 cur-grow"
          size="h1"
        />
      }
      width={548}
    >
      <ModalContent className="cur-bg-surface-neutral cur-p-4 cur-text-primary">
        {dialogHero && (
          <Image
            alt={dialogHero.altText || 'Hero image'}
            aspectRatio="3:2"
            src={dialogHero.url}
          />
        )}
        <Typography
          dangerouslySetInnerHTML={{ __html: displayTitle }}
          className="cur-mt-4"
          size="h2"
        />
        {dialogCallout && (
          <Highlight className="cur-mt-4" state="success">
            {dialogCallout}
          </Highlight>
        )}
        {dialogDescription && (
          <div
            className="cur-mt-4"
            dangerouslySetInnerHTML={{
              __html: convertSchemaToHtml(dialogDescription),
            }}
          ></div>
        )}
        {activeIngredients.length > 0 && (
          <>
            <Typography className="cur-mt-4" size="h3">
              Active Ingredients
            </Typography>
            <div className="cur-mt-4 cur-space-y-4">
              {activeIngredients.map((activeIngredient) => (
                <IngredientsAccordion
                  title={activeIngredient.title}
                  body={activeIngredient.short_title}
                  key={activeIngredient.title}
                />
              ))}
            </div>
          </>
        )}
        {inactiveIngredients.length > 0 && (
          <>
            <Typography className="cur-mt-4" size="h3">
              Ingredients
            </Typography>
            <Typography className="cur-mt-4">
              {inactiveIngredients.join(', ')}
            </Typography>
          </>
        )}
        {ingredientNotes && (
          <Typography
            dangerouslySetInnerHTML={{
              __html: convertSchemaToHtml(ingredientNotes),
            }}
            className="cur-mt-4"
          />
        )}
      </ModalContent>
      <ModalFooter>
        <Button
          className="cur-w-full"
          data-sentry-id="Close modal"
          onClick={onClose}
          size="lg"
          state="dark"
        >
          Done
        </Button>
      </ModalFooter>
    </Modal>
  );
};
